import { useEffect, useState } from 'react';
import { articles as articlesApi } from '@/api/articles';
import type { Site } from '@/types';

// useDraftCountsBySite returns { [siteId]: draftCount } for every site in
// `sites`. Fires one /articles list call per site in parallel and caches
// the result at module scope for 60s so reopening the switcher dropdown
// stays cheap. Failures per site fall back to 0 (the count is decorative,
// not a correctness signal).

interface CacheEntry {
  data: Record<string, number>;
  expires: number;
}

let cache: CacheEntry | null = null;

export function useDraftCountsBySite(sites: Site[]): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>(() =>
    cache && cache.expires > Date.now() ? cache.data : {},
  );
  useEffect(() => {
    if (sites.length === 0) return;
    if (cache && cache.expires > Date.now()) {
      setCounts(cache.data);
      return;
    }
    let cancelled = false;
    void Promise.all(
      sites.map((s) =>
        articlesApi
          .list(s.id, { status: 'draft' })
          .then((rows) => [s.id, rows.length] as const)
          .catch(() => [s.id, 0] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      const next = Object.fromEntries(pairs);
      cache = { data: next, expires: Date.now() + 60_000 };
      setCounts(next);
    });
    return () => {
      cancelled = true;
    };
  }, [sites]);
  return counts;
}
